import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from './auth/AuthLayout';
import { Banner, Field } from '../design/ui';



const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const checkUserAuthentication = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const response = await axios.get('/api/users/user', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.data.shop_name) {
            localStorage.setItem('shop', response.data.shop_name);
          }
          window.location.href = '/dashboard';
        }
      } catch (error) {
        localStorage.removeItem('access_token');
      }
    };

    checkUserAuthentication();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post('/api/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Store access token
      localStorage.setItem('access_token', response.data.access_token);

      // Fetch user data
      const userResponse = await axios.get('/api/users/user', {
        headers: {
          Authorization: `Bearer ${response.data.access_token}`,
        },
      });

      // Store user data including edn_api_account and fa_account if available
      const userData = {
        email: userResponse.data.email,
        role: userResponse.data.role,
        edn_api_account: userResponse.data.edn_api_account,
        edn_admin: userResponse.data.edn_admin,
        shop_name: userResponse.data.shop_name
      };
      
      if (userResponse.data.fa_account) {
        userData.fa_account = userResponse.data.fa_account;
      }
      
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('shop', userResponse.data.shop_name);

      // Add a small delay to ensure storage is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Final verification before navigation
      const finalCheck = {
        accessToken: localStorage.getItem('access_token'),
        shop: localStorage.getItem('shop')
      };
      console.log('Final storage check before navigation:', finalCheck);

      // Navigate if access token is properly stored
      if (finalCheck.accessToken) {
        window.location.href = '/dashboard';
      } else {
        console.error('Access token not properly stored before navigation');
        setLoginError(t('loginInvalidCredentials', 'Wrong email or password.'));
      }
    } catch (error) {
      console.error('Login failed:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('userData');
      setLoginError(t('loginInvalidCredentials', 'Wrong email or password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      heading={t('signIn', 'Connexion')}
      subheading={t('publicUi.signInSub', 'Accédez à l’espace de votre équipe.')}
    >
      <form className="u-form" onSubmit={handleLogin} noValidate={false}>
        {loginError && <Banner tone="danger">{loginError}</Banner>}
        <Field label={t('email', 'E-mail')} htmlFor="email">
          <input
            type="email"
            id="email"
            className="u-input"
            placeholder={t('enterEmail', 'Saisissez votre e-mail')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setLoginError('');
            }}
            aria-invalid={Boolean(loginError)}
            autoComplete="username"
            required
          />
        </Field>
        <Field label={t('password', 'Mot de passe')} htmlFor="password">
          <div className="u-input-group">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="u-input"
              placeholder={t('enterPassword', 'Saisissez votre mot de passe')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setLoginError('');
              }}
              aria-invalid={Boolean(loginError)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="u-input-group__btn"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? t('publicUi.hidePassword', 'Masquer le mot de passe') : t('publicUi.showPassword', 'Afficher le mot de passe')}
              title={showPassword ? t('publicUi.hidePassword', 'Masquer le mot de passe') : t('publicUi.showPassword', 'Afficher le mot de passe')}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </Field>
        <div className="auth-row">
          <span />
          <a className="u-link" href="/forgot-password">{t('forgotPassword', 'Mot de passe oublié ?')}</a>
        </div>
        <button type="submit" className="u-btn u-btn--primary u-btn--block" disabled={isSubmitting}>
          {isSubmitting ? t('common.loading', 'Chargement…') : t('login', 'Se connecter')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Login;
